import { MarkType, NodeType } from "../utils";
import { PlateElement, PlateElementProps, PlateLeaf } from "platejs/react";
import {
  MediaPlaceholder,
  PlateImageElement,
} from "../elements/MediaPlaceholder";
import {
  ColumnGroup,
  ColumnGroupStatic,
  ColumnItem,
  ColumnItemStatic,
} from "../elements/Columns";
import { Paragraph } from "../elements/Paragraph";
import { ReactNode } from "react";
import escapeHtml from "escape-html";
import { ScriptPlaceholder } from "../elements/ScriptPlaceholder";
import { UnknownElementPlaceholder } from "../elements/UnknownElementPlaceholder";
import { withNodeProps } from "./withNodeProps";
import type { SlateElementProps } from "platejs/static";
import type { TUnknownElement } from "./UnknownElementPlugin";
import type { TDivElement } from "./DivPlugin";
import type { TInlineElement } from "./InlineElementPlugin";
import { INLINE_UNKNOWN_TAG_NAMES } from "./UnknownElementPlugin";

export const markdownSupportedMarkTypes = [
  MarkType.STRONG,
  MarkType.EMPHASIS,
  MarkType.STRIKETHROUGH,
  MarkType.CODE,
];

export const markdownSupportedNodeTypes = [
  NodeType.PARAGRAPH,
  NodeType.ORDERED_LIST,
  NodeType.UNORDERED_LIST,
  NodeType.LIST_ITEM,
  NodeType.LIST_ITEM_CONTENT,
  NodeType.THEMATIC_BREAK,
  NodeType.BLOCK_QUOTE,
  NodeType.HEADING_ONE,
  NodeType.HEADING_TWO,
  NodeType.HEADING_THREE,
  NodeType.HEADING_FOUR,
  NodeType.HEADING_FIVE,
  NodeType.HEADING_SIX,
  NodeType.LINK,
];

const htmlOnlySupportedMarkTypes = [
  MarkType.UNDERLINE,
  MarkType.HIGHLIGHT,
  MarkType.SUBSCRIPT,
  MarkType.SUPERSCRIPT,
];

// Standard HTML void elements - React throws if these are given children (or
// dangerouslySetInnerHTML), so NodeType.UNKNOWN_ELEMENT's static renderer must render them
// self-closing instead of passing through the (always-empty) Slate children.
const HTML_VOID_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

const htmlOnlySupportedNodeTypes = [
  NodeType.IMAGE,
  NodeType.MEDIA_PLACEHOLDER,
  NodeType.COLUMN_GROUP,
  NodeType.COLUMN_ITEM,
  NodeType.IFRAME,
  NodeType.DIV,
  NodeType.INLINE_ELEMENT,
  NodeType.SCRIPT,
  NodeType.UNKNOWN_ELEMENT,
  NodeType.STYLE,
  NodeType.TABLE,
  NodeType.TABLE_ROW,
  NodeType.TABLE_CELL,
  NodeType.TABLE_CELL_HEADER,
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const supportedNodeTypes = [
  ...markdownSupportedMarkTypes,
  ...markdownSupportedNodeTypes,
  ...htmlOnlySupportedNodeTypes,
  ...htmlOnlySupportedMarkTypes,
] as const;
type ISupportedNodeTypes = (typeof supportedNodeTypes)[number];

export const basePlateComponentOverrides: {
  [key in ISupportedNodeTypes]: (props) => ReactNode;
} = {
  [MarkType.STRONG]: (props) => <PlateLeaf as={"strong"} {...props} />,
  [MarkType.EMPHASIS]: (props) => <PlateLeaf as={"em"} {...props} />,
  [MarkType.UNDERLINE]: (props) => <PlateLeaf as={"u"} {...props} />,
  [MarkType.STRIKETHROUGH]: (props) => <PlateLeaf as={"s"} {...props} />,
  [MarkType.CODE]: (props) => <PlateLeaf as={"code"} {...props} />,
  [MarkType.HIGHLIGHT]: (props) => <PlateLeaf as={"mark"} {...props} />,
  [MarkType.SUBSCRIPT]: (props) => <PlateLeaf as={"sub"} {...props} />,
  [MarkType.SUPERSCRIPT]: (props) => <PlateLeaf as={"sup"} {...props} />,
  [NodeType.BLOCK_QUOTE]: (props) => (
    <PlateElement as={"blockquote"} {...props} />
  ),
  [NodeType.HEADING_ONE]: (props) => <PlateElement as={"h1"} {...props} />,
  [NodeType.HEADING_TWO]: (props) => <PlateElement as={"h2"} {...props} />,
  [NodeType.HEADING_THREE]: (props) => <PlateElement as={"h3"} {...props} />,
  [NodeType.HEADING_FOUR]: (props) => <PlateElement as={"h4"} {...props} />,
  [NodeType.HEADING_FIVE]: (props) => <PlateElement as={"h5"} {...props} />,
  [NodeType.HEADING_SIX]: (props) => <PlateElement as={"h6"} {...props} />,
  [NodeType.PARAGRAPH]: (props) => <Paragraph {...props} />,
  [NodeType.UNORDERED_LIST]: (props) => <PlateElement as={"ul"} {...props} />,
  [NodeType.ORDERED_LIST]: (props) => <PlateElement as={"ol"} {...props} />,
  [NodeType.LIST_ITEM]: (props) => <PlateElement as={"li"} {...props} />,
  [NodeType.LIST_ITEM_CONTENT]: (props) => props.children,
  [NodeType.LINK]: (props) => <PlateElement as={"a"} {...props} />,
  [NodeType.THEMATIC_BREAK]: () => <hr contentEditable={false} />,
  [NodeType.IMAGE]: (props) => {
    const { url, alt } = props.element;
    return <PlateImageElement src={url} alt={alt} />;
  },
  [NodeType.MEDIA_PLACEHOLDER]: (props) => <MediaPlaceholder {...props} />,
  [NodeType.COLUMN_GROUP]: (props) => <ColumnGroup {...props} />,
  [NodeType.COLUMN_ITEM]: (props) => <ColumnItem {...props} />,
  [NodeType.IFRAME]: (props) => {
    return <iframe {...props.element.elementAttributes} />;
  },
  [NodeType.SCRIPT]: () => <ScriptPlaceholder />,
  [NodeType.STYLE]: () => <ScriptPlaceholder tag={"style"} />,
  [NodeType.DIV]: (props: PlateElementProps<TDivElement>) => (
    <PlateElement as={props.element.tagName ?? "div"} {...props} />
  ),
  [NodeType.INLINE_ELEMENT]: (props: PlateElementProps<TInlineElement>) => (
    <PlateElement as={props.element.tagName ?? "span"} {...props} />
  ),
  // Void, so it's click-to-select and backspace-deletable as one atomic block, same as an
  // image - the tag, its attributes, and its original inner markup are preserved and reappear
  // on save if left alone (see the static map below). props.children must still be rendered:
  // for a void it's Slate's hidden spacer text node, which is where Slate maps any click inside
  // the void - without it the native caret lands wherever the browser puts it.
  [NodeType.UNKNOWN_ELEMENT]: (props) => {
    const Wrapper = INLINE_UNKNOWN_TAG_NAMES.has(props.element.tagName)
      ? "span"
      : "div";
    return (
      <Wrapper
        {...props.attributes}
        style={{ position: "relative", ...props.attributes?.style }}
      >
        <UnknownElementPlaceholder tagName={props.element.tagName} />
        {props.children}
      </Wrapper>
    );
  },
  [NodeType.TABLE]: (props) => <PlateElement as={"table"} {...props} />,
  [NodeType.TABLE_ROW]: (props) => <PlateElement as={"tr"} {...props} />,
  [NodeType.TABLE_CELL]: (props) => <PlateElement as={"td"} {...props} />,
  [NodeType.TABLE_CELL_HEADER]: (props) => (
    <PlateElement as={"th"} {...props} />
  ),
};

export const staticPlateComponents: {
  [key in ISupportedNodeTypes]: (props) => ReactNode;
} = {
  [MarkType.STRONG]: (props) => (
    <strong {...withNodeProps(props)}>{props.children}</strong>
  ),
  [MarkType.EMPHASIS]: (props) => (
    <em {...withNodeProps(props)}>{props.children}</em>
  ),
  [MarkType.UNDERLINE]: (props) => (
    <u {...withNodeProps(props)}>{props.children}</u>
  ),
  [MarkType.STRIKETHROUGH]: (props) => (
    <s {...withNodeProps(props)}>{props.children}</s>
  ),
  [MarkType.CODE]: (props) => {
    return <code {...withNodeProps(props)}>{escapeHtml(props.text.text)}</code>;
  },
  [MarkType.HIGHLIGHT]: (props) => (
    <mark {...withNodeProps(props)}>{props.children}</mark>
  ),
  [MarkType.SUBSCRIPT]: (props) => (
    <sub {...withNodeProps(props)}>{props.children}</sub>
  ),
  [MarkType.SUPERSCRIPT]: (props) => (
    <sup {...withNodeProps(props)}>{props.children}</sup>
  ),
  [NodeType.BLOCK_QUOTE]: (props) => (
    <blockquote {...withNodeProps(props)}>{props.children}</blockquote>
  ),
  [NodeType.HEADING_ONE]: (props) => (
    <h1 {...withNodeProps(props)}>{props.children}</h1>
  ),
  [NodeType.HEADING_TWO]: (props) => (
    <h2 {...withNodeProps(props)}>{props.children}</h2>
  ),
  [NodeType.HEADING_THREE]: (props) => (
    <h3 {...withNodeProps(props)}>{props.children}</h3>
  ),
  [NodeType.HEADING_FOUR]: (props) => (
    <h4 {...withNodeProps(props)}>{props.children}</h4>
  ),
  [NodeType.HEADING_FIVE]: (props) => (
    <h5 {...withNodeProps(props)}>{props.children}</h5>
  ),
  [NodeType.HEADING_SIX]: (props) => (
    <h6 {...withNodeProps(props)}>{props.children}</h6>
  ),
  [NodeType.PARAGRAPH]: (props) => {
    return <p {...withNodeProps(props)}>{props.children}</p>;
  },
  [NodeType.UNORDERED_LIST]: (props) => (
    <ul {...withNodeProps(props)}>{props.children}</ul>
  ),
  [NodeType.ORDERED_LIST]: (props) => (
    <ol {...withNodeProps(props)}>{props.children}</ol>
  ),
  [NodeType.LIST_ITEM]: (props) => (
    <li {...withNodeProps(props)}>{props.children}</li>
  ),
  [NodeType.LIST_ITEM_CONTENT]: (props) => props.children,
  [NodeType.LINK]: (props) => <a {...withNodeProps(props)}>{props.children}</a>,
  [NodeType.THEMATIC_BREAK]: () => <hr contentEditable={false} />,
  [NodeType.IMAGE]: (props) => (
    <img
      {...withNodeProps(props)}
      src={props.element.url}
      alt={props.element.alt}
    />
  ),
  [NodeType.COLUMN_GROUP]: (props) => <ColumnGroupStatic {...props} />,
  [NodeType.COLUMN_ITEM]: (props) => <ColumnItemStatic {...props} />,
  [NodeType.MEDIA_PLACEHOLDER]: (props) => {
    switch (props.element.mediaType) {
      case "img":
        return (
          <img
            alt={props.element.alt}
            role={props.element.elementAttributes?.role}
            data-id={props.element.id}
            data-media-type={props.element.mediaType}
            data-type={props.element.type}
          />
        );
      default:
        return <></>;
    }
  },
  [NodeType.IFRAME]: (props) => {
    return <iframe {...withNodeProps(props)} />;
  },
  [NodeType.SCRIPT]: (props) => {
    return <script {...withNodeProps(props)} />;
  },
  [NodeType.STYLE]: (props) => {
    return <style {...withNodeProps(props)} />;
  },
  [NodeType.DIV]: (props: SlateElementProps<TDivElement>) => {
    const Tag = props.element.tagName ?? "div";
    return <Tag {...withNodeProps(props)}>{props.children}</Tag>;
  },
  [NodeType.INLINE_ELEMENT]: (props: SlateElementProps<TInlineElement>) => {
    const Tag = props.element.tagName ?? "span";
    return <Tag {...withNodeProps(props)}>{props.children}</Tag>;
  },
  [NodeType.UNKNOWN_ELEMENT]: (props: SlateElementProps<TUnknownElement>) => {
    const Tag = props.element.tagName;
    const nodeProps = withNodeProps(props);
    if (HTML_VOID_TAGS.has(Tag)) {
      // True HTML void tags (embed, source, ...) never have children, and React throws if
      // they're given dangerouslySetInnerHTML at all - even an empty one.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { dangerouslySetInnerHTML, ...rest } = nodeProps;
      return <Tag {...rest} />;
    }
    return <Tag {...nodeProps} />;
  },
  [NodeType.TABLE]: (props) => (
    <table {...withNodeProps(props)}>
      <tbody>{props.children}</tbody>
    </table>
  ),
  [NodeType.TABLE_ROW]: (props) => (
    <tr {...withNodeProps(props)}>{props.children}</tr>
  ),
  [NodeType.TABLE_CELL]: (props) => (
    <td {...withNodeProps(props)}>{props.children}</td>
  ),
  [NodeType.TABLE_CELL_HEADER]: (props) => (
    <th {...withNodeProps(props)}>{props.children}</th>
  ),
};
